/**
 * Header and pagination controls for the Part Schedule table.
 *
 * Split out of PartSchedule.tsx to keep that page within the file-size limit.
 *
 * @link PartSchedulePage for the page that composes these.
 */
import { TableHead } from '../ui/table';
import { Button } from '../ui/button';
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Columns the server can sort on.
 *
 * Supplier and Supplier Part No are deliberately absent: both are multi-valued
 * for a dual-sourced part, so there is no single value to order a row by.
 */
export type SortKey =
  | 'part_number'
  | 'description'
  | 'plant'
  | 'schedule_firm'
  | 'active'
  | 'updated_at';

interface SortableHeadProps {
  /** Visible column label. */
  label: string;
  /** Ordering key sent to the API for this column. */
  sortKey: SortKey;
  /** Currently active sort key. */
  currentSort: SortKey;
  /** Whether the active sort is descending. */
  descending: boolean;
  onSort: (key: SortKey) => void;
  className?: string;
}

/**
 * Table header that toggles asc/desc on click and shows the active direction.
 *
 * @param props - See {@link SortableHeadProps}
 * @returns A header cell with a sort affordance.
 */
export function SortableHead({
  label,
  sortKey,
  currentSort,
  descending,
  onSort,
  className,
}: SortableHeadProps) {
  const active = currentSort === sortKey;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
      >
        {label}
        {active ? (
          descending ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    </TableHead>
  );
}

interface PaginationControlsProps {
  page: number;
  totalPages: number;
  disabled: boolean;
  onChange: (page: number) => void;
}

/**
 * Prev/Next pager. Rendered above *and* below the table so a full page of rows
 * doesn't force a scroll back up to change page.
 *
 * @param props - See {@link PaginationControlsProps}
 * @returns The pager row.
 */
export function PaginationControls({
  page,
  totalPages,
  disabled,
  onChange,
}: PaginationControlsProps) {
  return (
    <div className="flex items-center justify-center gap-2">
      <Button
        size="sm"
        variant="outline"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1 || disabled}
      >
        <ChevronLeft className="h-4 w-4" />
        Prev
      </Button>
      <span className="text-xs text-muted-foreground">
        Page {page} of {totalPages}
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages || disabled}
      >
        Next
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
